import { ITag } from "@app/interfaces/ITag";
import { IVeriListRow } from "@directives/_veri/veriList/veriList.component";
import { ResourceAllocation, ResourceType } from "@enums/ResourceType";
import { IDriver } from "@interfaces/IDriver";
import { IGeofence } from "@interfaces/IGeofences";
import { IKeyValuePair } from "@interfaces/IKeyValuePair";
import { IVehicle } from "@interfaces/IVehicle";
import { ColourService, IColour } from "@services/colour.service";
import { DriverService } from "@services/driver.service";
import { EnumService } from "@services/enum.service";
import { GeofenceService } from "@services/geofence.service";
import { TagsService } from "@services/tags.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import * as angular from "angular";
import "./geofenceTagsEdit.scss";

class GeofenceTagsEditComponentController implements angular.IComponentController {

    tagRows: IVeriListRow<ITag>[] = [];

    onGeofenceTagDeleted: (args: { tag: ITag }) => void;

    tags: ITag[] = [];
    drivers: IDriver[] = [];
    vehicles: IVehicle[] = [];
    geofences: IGeofence[] = [];

    maxTagPosition: number = 1;
    availableColours: IColour[];
    selectedTag: ITag;

    hideDeleteButton: boolean;
    disableSave: boolean = false;

    showChangeInfo: boolean = false;

    resourceType: ResourceAllocation;

    // enums
    ResourceAllocation = ResourceAllocation;
    resourceAllocationTypes: IKeyValuePair<typeof ResourceAllocation>[];

    clashedResources: string[] = [];

    loading: boolean = true;

    static $inject = ["$q", "$scope", "$timeout", "$mdDialog", "tagsService", "colourService", "driverService", "vehicleService", "geofenceService", "translationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $scope: angular.IScope,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly tagsService: TagsService,
        private readonly colourService: ColourService,
        private readonly driverService: DriverService,
        private readonly vehicleService: VehicleService,
        private readonly geofenceService: GeofenceService,
        private readonly translationService: TranslationService
    ) {
    }

    $onInit(): void {
        this.setupEnumArrays();

        this.availableColours = this.colourService.tagColours;

        this.loadData();
    }

    addNewTag(): void {
        var newTag: ITag = {
            Id: 0,
            Name: this.translationService.t("geofenceTagsEdit.newTag"),
            ResourceType: ResourceType.Geofence,
            ColourObject: this.availableColours[7],
            Colour: this.availableColours[7].colourEnum,
            Position: this.tags.length + 1,
            ResourceAllocation: ResourceAllocation.All
        };

        this.setSelectedTag(newTag);
    }

    deleteTag() {
        const dialog = this.$mdDialog
            .confirm()
            .title(this.translationService.t('geofenceTagsEdit.deleteTags'))
            .textContent(this.translationService.t('geofenceTagsEdit.deleteTagsContent'))
            .ok(this.translationService.t('geofenceTagsEdit.deleteTag'))
            .cancel(this.translationService.t('Common.Cancel'))
            .multiple(true);

        this.$mdDialog
            .show(dialog)
            .then(() => {
                this.onDeleteConfirm();
            });
    }


    resourceChanged() {
        this.showChangeInfo = false;
        this.disableSave = false;
        this.clashedResources = [];

        if (this.selectedTag.ResourceAllocation == this.resourceType) {
            return;
        }

        if (this.resourceType !== ResourceAllocation.All) {
            this.checkDuplicateResources();
        }
    }

    save(): void {
        if (!this.selectedTag) {
            return;
        }

        this.loading = true;

        this.tagsService.postTag(this.selectedTag).finally(() => {
            this.selectedTag = null;

            this.$timeout(() => {
                this.loadData(true);
            }, 1500);
        });
    }

    setSelectedTag(tag: ITag, forceSelect: boolean = false) {
        this.onHideDeleteButton(tag);
        this.showChangeInfo = false;
        this.disableSave = false;

        if (this.selectedTag && this.selectedTag.Id === tag.Id && !forceSelect) {
            this.selectedTag = null;
            return;
        }
        this.selectedTag = angular.copy(tag);

        this.resourceType = this.selectedTag.ResourceAllocation;
    }

    selectTagColour(colour: IColour): void {
        if (!colour) {
            this.disableSave = true;
            return;
        }
        else {
            this.disableSave = false;
        }

        this.selectedTag.ColourObject = colour;
        this.selectedTag.Colour = colour.colourEnum;
    }

    //----------------------------------------------------

    private createTagRows(): void {
        this.tagRows = [];

        if (!this.tags) {
            return;
        }

        this.tags.forEach(tag => {
            if (!tag.Active) {
                return;
            }

            this.tagRows.push({
                id: tag.Id,
                contents: tag.Name,
                data: tag,
                sortData: tag.Position
            });
        });

        this.maxTagPosition = this.tags.length + 1;
    }

    private checkDuplicateResources() {
        if (this.resourceType == ResourceAllocation.Driver) {
            this.drivers.forEach(driver => {
                if (driver.TagIds.find(id => id == this.selectedTag.Id)) {
                    this.clashedResources.push(driver.Name);
                }
            });
        }

        if (this.resourceType == ResourceAllocation.Vehicle) {
            this.vehicles.forEach(vehicle => {
                if (vehicle.TagIds.find(id => id == this.selectedTag.Id)) {
                    this.clashedResources.push(vehicle.Reg);
                }
            });
        }

        if (this.resourceType == ResourceAllocation.Geofence) {
            this.geofences.forEach(geofence => {
                if (geofence.TagIds.find(id => id == this.selectedTag.Id)) {
                    this.clashedResources.push(geofence.Description);
                }
            });
        }

        if (this.clashedResources.length > 0) {
            this.showChangeInfo = true;
            this.disableSave = true;
        }
    };

    private onDeleteConfirm() {
        this.selectedTag.Active = false;

        this.loading = true;
        this.tagsService.postTag(this.selectedTag).then(tag => {
            this.onGeofenceTagDeleted({ tag });

            this.setSelectedTag(tag);
            this.loadData();
        });
    }

    private onHideDeleteButton(tag: ITag) {
        if (tag.AutoTag || tag.Id == -1) {
            this.hideDeleteButton = true;
        }
        else {
            this.hideDeleteButton = false;
        }
    }

    private loadData(invalidateCache = false) {
        this.loading = true;

        const tagsPromise = this.tagsService.getTags({ invalidateCache });
        const driversPromise = this.driverService.getDrivers({ includeTags: true });
        const vehiclesPromise = this.vehicleService.getVehicles({ includeTags: true, includePeripheralMappings: true });
        const geofencePromise = this.geofenceService.getGeofences({ includeTags: true });

        this.$q.all([tagsPromise, driversPromise, vehiclesPromise, geofencePromise])
            .then(([tags, drivers, vehicles, geofences]) => {

                this.tags = tags;
                this.drivers = drivers;
                this.vehicles = vehicles;
                this.geofences = geofences;

            })
            .finally(() => {
                this.createTagRows();

                this.loading = false;
            });
    }

    private setupEnumArrays(): void {
        this.resourceAllocationTypes = EnumService.enumToArray("ResourceAllocation", ResourceAllocation);
        this.resourceAllocationTypes.sort((type1, type2) => (type1.value > type2.value) ? 1 : -1);
    }
}

export class GeofenceTagsEditComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            onGeofenceTagDeleted: "&"
        };

        this.controller = GeofenceTagsEditComponentController;
        this.template = require("./geofenceTagsEdit.html");
    }
}
